import { useState } from 'react';

interface LobbyProps {
  onCreateRoom: (playerName: string) => void;
  onJoinRoom: (roomCode: string, playerName: string) => void;
}

export default function Lobby({ onCreateRoom, onJoinRoom }: LobbyProps) {
  const [name, setName] = useState(() => localStorage.getItem('ttt-name') || '');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<'menu' | 'join'>('menu');

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0;

  const rememberName = () => {
    if (trimmedName) localStorage.setItem('ttt-name', trimmedName);
  };

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center gap-6 px-4">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-5xl font-display font-bold mb-2">
          <span className="text-xpiece drop-shadow-[0_0_18px_rgba(255,107,107,0.5)]">X</span>
          <span className="text-ttt-muted text-3xl">vs</span>
          <span className="text-opiece drop-shadow-[0_0_18px_rgba(78,205,196,0.5)]">O</span>
        </div>
        <h1 className="text-ttt-ink text-xl font-display font-semibold">Tic Tac Toe</h1>
        <p className="text-ttt-muted text-sm mt-1">Line up three in a row to win.</p>
      </div>

      <div className="ttt-glass rounded-2xl p-6 w-full flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-ttt-muted text-xs uppercase tracking-wider font-semibold">Your name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            placeholder="e.g. Subham"
            className="bg-ttt-bg/70 border border-ttt-border rounded-xl px-4 py-3 text-ttt-ink placeholder:text-ttt-muted/50 outline-none focus:border-ttt-accent transition-colors"
          />
        </label>

        {mode === 'menu' ? (
          <>
            <button
              disabled={!canSubmit}
              onClick={() => {
                rememberName();
                onCreateRoom(trimmedName);
              }}
              className="ttt-gradient rounded-xl px-4 py-3.5 font-display font-semibold text-lg text-ttt-bg bg-gradient-to-r from-ttt-accent to-xpiece disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98] transition shadow-glow"
            >
              Create Room
            </button>
            <button
              disabled={!canSubmit}
              onClick={() => setMode('join')}
              className="rounded-xl px-4 py-3 font-display font-medium text-ttt-ink border border-ttt-border bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/10 active:scale-[0.98] transition"
            >
              Join with Code
            </button>
          </>
        ) : (
          <>
            <label className="flex flex-col gap-1.5">
              <span className="text-ttt-muted text-xs uppercase tracking-wider font-semibold">Room code</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                placeholder="ABC123"
                autoCapitalize="characters"
                className="bg-ttt-bg/70 border border-ttt-border rounded-xl px-4 py-3 text-ttt-ink placeholder:text-ttt-muted/50 outline-none focus:border-ttt-accent transition-colors text-center text-2xl font-display tracking-[0.35em] uppercase"
              />
            </label>
            <button
              disabled={!canSubmit || code.trim().length !== 6}
              onClick={() => {
                rememberName();
                onJoinRoom(code.trim(), trimmedName);
              }}
              className="ttt-gradient rounded-xl px-4 py-3.5 font-display font-semibold text-lg text-ttt-bg bg-gradient-to-r from-ttt-accent to-opiece disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98] transition shadow-glow"
            >
              Join Room
            </button>
            <button
              onClick={() => setMode('menu')}
              className="text-ttt-muted text-sm hover:text-ttt-ink transition-colors"
            >
              ← Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
